import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { UserButton, useClerkAppearance } from '../../auth/clerk';
import { useTheme } from '../../context/ThemeContext';
import { useApp } from '../../context/AppContext';
import { SidebarTabs } from '../Sidebar';
import LeftNav from './LeftNav';
import RoomChatPane, { chatPaneSubject } from '../Chat/RoomChatPane';
import { RoomChatDraftProvider } from '../Chat/roomChatDraft';
import { roomKeyForSubject } from '../../utils/roomChat';
import AtlasIcon from '../icons/AtlasIcon';
import Wordmark from '../Brand/Wordmark';
import RailTooltip from '../Common/RailTooltip';
import { GuideButton, GuidePanel } from '../Guide';
import { CapturePanel } from '../Captures';
import BookmarksPanel from '../Bookmarks/BookmarksPanel';

import { LearnPanel } from '../LearnPanel';
import { DebugPanel } from '../DebugPanel';
import NameWizardPanel from '../Names/NameWizardPanel';
import { SettingsDialog } from '../Settings';
import { BugReportModal } from '../BugReporter';
import { bugsApi } from '../../api/bugs';
import { sessionsApi } from '../../api/sessions';
import { storiesApi } from '../../api/stories';
import { disambiguateName } from '../../utils/disambiguateName';
import { plottrackPathForBook } from '../../utils/plottrackPath';
import { pathRoomPathForBook } from '../../utils/pathRoomPath';
import { styleCardsPathForBook } from '../../utils/styleCardsPath';
import { storyRulesPath } from '../../utils/storyRulesPath';
import { sceneWritingPathForBook } from '../../utils/sceneWritingPath';
import { runAfterLeaveGuard } from '../../utils/draftLeaveGuard';
import { openBookChatPath } from '../../utils/openBookChat';
import { IDEA_ROOM_TITLE } from '../../utils/writerCopy';
import BookUploadDoor from '../DocumentImport/BookUploadDoor';
import { FrameworkUndoToast } from '../PlotTrack/UndoMessage';
import {
  breadcrumbRoomFromPath,
  showLegacyAppBar,
  showRightPanelSlot,
  showBookToolsRow,
  isSessionSittingPath,
} from './sidebarMap';
import { publishChatUnfold } from '../Chat/ChatPaneFrame';
import { BookToolsProvider, BOOK_TOOLS_EVENT, bookToolShouldUnfoldChat } from '../BookTools/bookTools';
import RightPanelSwitcher from './RightPanelSwitcher';
import StoryProgressPanel from '../StoryProgress/StoryProgressPanel';
import StoryProgressSheet, { STORY_PROGRESS_SHEET_TITLE } from '../StoryProgress/StoryProgressSheet';
import { RIGHT_SLOT, clampRightSlotWidth } from './rightSlotWidth';

import usersApi from '../../api/users';
import { settingsApi } from '../../api/settings';
import { useBugCapture } from '../../hooks/useBugCapture';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { FuelGauge } from '../FuelGauge/FuelGauge';
import MobileHeader from './MobileHeader';
import { registerSettingsOpener } from '../../utils/openSettings';
import MobileDrawer from './MobileDrawer';
import MobileTabBar from './MobileTabBar';
import { TextSizeStepper } from '../Common/TextSizeStepper';
import { CHAT_TEXT_SIZE, SIDEBAR_TEXT_SIZE, readChatZoom, readStoredPercent } from '../../utils/textSizeRange';
import './BrainstormyLayout.css';

// Last beta-status answer. The header icon is gated on
// bugsApi.checkBetaAccess(), which only returns once auth and the
// page request finish — about 8s after a reload. The cache paints the
// same answer with the header. Unknown stays a reserved slot.
export const HELP_ICON_CACHE_KEY = 'brainstormy.helpFeedback.visible';

export function readHelpIconCache() {
  try {
    const value = sessionStorage.getItem(HELP_ICON_CACHE_KEY);
    if (value === '1') return true;
    if (value === '0') return false;
  } catch (e) {
    // Private browsing can throw. The live check still runs.
  }
  return null;
}

function writeHelpIconCache(allowed) {
  try {
    sessionStorage.setItem(HELP_ICON_CACHE_KEY, allowed ? '1' : '0');
  } catch (e) {
    // Ignore storage failures. This visit still follows the live check.
  }
}

const STORAGE_KEYS = {
  sidebarWidth: 'brainstormy.jetage.sidebarWidth',
  // v2: bumped so the stale saved 100 is ignored and the new default (130) lands
  // once for existing users (Item 1).
  sidebarZoom: 'brainstormy.jetage.sidebarZoom.v2',
  sidebarCollapsed: 'brainstormy.jetage.sidebarCollapsed',
  guidePanelWidth: 'brainstormy.jetage.guidePanelWidth',
  guidePanelOpen: 'brainstormy.jetage.guidePanelOpen',
  capturePanelWidth: 'brainstormy.jetage.capturePanelWidth',
  capturePanelOpen: 'brainstormy.jetage.capturePanelOpen',
  bookmarksPanelWidth: 'brainstormy.jetage.bookmarksPanelWidth',
  bookmarksPanelOpen: 'brainstormy.jetage.bookmarksPanelOpen',
  learnPanelWidth: 'brainstormy.jetage.learnPanelWidth',
  learnPanelOpen: 'brainstormy.jetage.learnPanelOpen',
  debugPanelWidth: 'brainstormy.jetage.debugPanelWidth',
  debugPanelOpen: 'brainstormy.jetage.debugPanelOpen',
  namesPanelWidth: 'brainstormy.jetage.namesPanelWidth',
  namesPanelOpen: 'brainstormy.jetage.namesPanelOpen',
  rightSlotWidth: 'brainstormy.jetage.rightSlotWidth',
};

const SIDEBAR = {
  minWidth: 220,
  maxWidth: 500,
  defaultWidth: 300,
  collapsedWidth: 56,
  minZoom: SIDEBAR_TEXT_SIZE.min,
  maxZoom: SIDEBAR_TEXT_SIZE.max,
  defaultZoom: SIDEBAR_TEXT_SIZE.default,
};

const GUIDE_PANEL = {
  minWidth: 320,
  maxWidth: 600,
  defaultWidth: 400,
};

const TASK_PANEL = {
  minWidth: 280,
  maxWidth: 500,
  defaultWidth: 360,
};

const BOOKMARKS_PANEL = {
  minWidth: 320,
  maxWidth: 500,
  defaultWidth: 380,
};

const LEARN_PANEL = {
  minWidth: 360,
  maxWidth: 550,
  defaultWidth: 400,
};

const DEBUG_PANEL = {
  minWidth: 380,
  maxWidth: 600,
  defaultWidth: 450,
};

const NAMES_PANEL = {
  minWidth: 340,
  maxWidth: 520,
  defaultWidth: 400,
};

// Tool definitions for the tools bar
// bible-consolidation: Bookmarks moved into the Compendium panel as a tab, so the
// standalone desktop Bookmarks tool is retired here. The panel wiring + mobile
// header affordance remain (both render the shared BookmarksView).
// Top ToolsBar (Reference + Find). `icon` is the AtlasIcon mark name.
// Bookmarks is absent by design. Book Notes lives in LeftNav next to
// Story Board — not here (it is not a bible, and not a reference tool).
// Elements is retired: its writer-facing roster duplicated Book Notes.
const TOOLS = [
  { id: 'captures', label: 'Compendium', icon: 'compendium', panel: true },
  { id: 'learn', label: 'Research', icon: 'research', panel: true },
  { id: 'names', label: 'Names', icon: 'spark', panel: true, storyOnly: true },
  { id: 'search', label: 'Search', icon: 'search', path: '/search' },
  // Orientation Dashboard (replaces StoryGraph). Story-scoped; no badge —
  // the dashboard is a read-only derived panel with no pending concept.
  { id: 'graph', label: 'Story Dashboard', icon: 'dashboard', path: '/graph', storyOnly: true },
];

// The capped logo and the ? control share the row from 297px up
// (both layouts measure 126.92px). Below that the control moves into
// the settings menu, including under 260px, so the mark does not step.
const HELP_PARK_BELOW = 297;

function useHeaderBoxWidth(headerRef, isCollapsed, sidebarWidth) {
  const [measured, setMeasured] = useState(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || isCollapsed || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const next = el.clientWidth;
      if (next > 0) setMeasured(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [headerRef, isCollapsed]);
  return measured == null ? sidebarWidth : measured;
}

function HelpFeedbackButton({ visible, reserved, onClick, className }) {
  if (!visible && !reserved) return null;
  return (
    <button
      type="button"
      className={`${className} brainstormy-sidebar__help-icon${visible ? '' : ' brainstormy-sidebar__icon-slot--reserved'}`}
      onClick={visible ? onClick : undefined}
      title={visible ? 'Help and feedback' : undefined}
      aria-label={visible ? 'Help and feedback' : undefined}
      aria-hidden={visible ? undefined : true}
      tabIndex={visible ? undefined : -1}
      disabled={!visible}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </button>
  );
}

/**
 * Sidebar Header with Logo and Settings
 */
export function SidebarHeader({
  onSettingsClick,
  showBugButton,
  reserveBugSlot,
  onBugReportClick,
  isCollapsed,
  onToggleCollapse,
  sidebarWidth = 300,
}) {
  const clerkAppearance = useClerkAppearance({ avatarBox: 'brainstormy-sidebar__user-avatar' });
  const headerRef = useRef(null);
  const toolsRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerWidth = useHeaderBoxWidth(headerRef, isCollapsed, sidebarWidth);
  const parkHelp = Boolean(showBugButton) && !isCollapsed && headerWidth < HELP_PARK_BELOW;

  useEffect(() => {
    if (!parkHelp) setMenuOpen(false);
  }, [parkHelp]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        settingsBtnRef.current?.focus();
      }
    };
    const onPointer = (event) => {
      if (!toolsRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [menuOpen]);

  const openSettings = () => {
    setMenuOpen(false);
    onSettingsClick?.();
  };

  return (
    <div
      ref={headerRef}
      className={`brainstormy-sidebar__header ${isCollapsed ? 'brainstormy-sidebar__header--collapsed' : ''}`}
    >
      {/* Approved no-tagline wordmark. The 56px collapsed rail is below the
          100px minimum, so the mark is omitted there rather than cropped or
          paired with the B icon. The icon cluster stays on one row. Below
          297px the admin help control moves into the settings menu. */}
      {!isCollapsed && (
        <div className="brainstormy-sidebar__header-bar">
          <div className="brainstormy-sidebar__brand-row">
            <Wordmark placement="header" className="brainstormy-sidebar__wordmark" />
          </div>
          <div className="brainstormy-sidebar__header-tools" ref={toolsRef}>
            <HelpFeedbackButton
              visible={Boolean(showBugButton) && !parkHelp}
              reserved={Boolean(reserveBugSlot) && !showBugButton}
              onClick={onBugReportClick}
              className="brainstormy-sidebar__bug-btn"
            />
            <button
              type="button"
              ref={settingsBtnRef}
              className="brainstormy-sidebar__settings-btn"
              onClick={parkHelp ? () => setMenuOpen((open) => !open) : openSettings}
              title="Settings"
              aria-label="Settings"
              aria-haspopup={parkHelp ? 'menu' : undefined}
              aria-expanded={parkHelp ? menuOpen : undefined}
              aria-controls={menuOpen ? 'sidebar-settings-menu' : undefined}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
            <UserButton
              afterSignOutUrl="/sign-in"
              appearance={clerkAppearance}
            />
            {/* #802: hide control must be findable. A 14px dim chevron in
                the header cluster is not enough. Expanded-state only —
                the collapsed rail has its own Show left panel tab. */}
            <button
              type="button"
              className="brainstormy-sidebar__collapse-btn"
              onClick={onToggleCollapse}
              title="Hide left panel"
              aria-label="Hide left panel"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {menuOpen && (
              <div
                id="sidebar-settings-menu"
                className="brainstormy-sidebar__settings-menu"
                role="menu"
                aria-label="Settings"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="brainstormy-sidebar__settings-item"
                  onClick={openSettings}
                >
                  Settings
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="brainstormy-sidebar__settings-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onBugReportClick?.();
                  }}
                >
                  Help and feedback
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Tools Bar - right-aligned tool buttons with Guide button
 */
export function ToolsBar({ activeTool, isCapturePanelOpen, isBookmarksPanelOpen, isLearnPanelOpen, isNamesPanelOpen, isDebugPanelOpen, hasProject, hasStory, isAdmin, onToolClick, isGuideOpen, onGuideToggle, guideLevel, hasGuideContext }) {
  // The StoryGraph pending-count badge was removed with the dashboard swap:
  // the Story Dashboard is read-only and has no pending concept. The
  // 'graph:pending-count-changed' event chat.py still dispatches is now a
  // harmless no-op (no listener).

  return (
    <div className="brainstormy-tools-bar">
      {TOOLS.filter(t => !t.hidden).map((tool) => {
        // Outline stays in the left column (Develop), not the top bar.
        // Hide story-only tools when no story is selected
        if (tool.storyOnly && !hasStory) {
          return null;
        }

        // For panel-based tools, use their respective state; for others, use URL-based activeTool
        let isActive = activeTool === tool.id;
        if (tool.id === 'captures') isActive = isCapturePanelOpen;
        if (tool.id === 'bookmarks') isActive = isBookmarksPanelOpen;
        if (tool.id === 'learn') isActive = isLearnPanelOpen;
        if (tool.id === 'names') isActive = isNamesPanelOpen;

        // Disable panel buttons when no project is selected
        const isDisabled = tool.panel && !hasProject;

        return (
          <button
            key={tool.id}
            className={`brainstormy-tools-bar__button ${isActive ? 'brainstormy-tools-bar__button--active' : ''} ${isDisabled ? 'brainstormy-tools-bar__button--disabled' : ''}`}
            onClick={() => !isDisabled && onToolClick(tool)}
            disabled={isDisabled}
            title={isDisabled ? 'Select a session first' : tool.label}
          >
            <span className="brainstormy-tools-bar__icon"><AtlasIcon name={tool.icon} size={15} /></span>
            <span>{tool.label}</span>
          </button>
        );
      })}
      {/* Debug and the writer roster — only for admin users */}
      {isAdmin && (
        <button
          type="button"
          className={`brainstormy-tools-bar__button brainstormy-tools-bar__button--debug ${isDebugPanelOpen ? 'brainstormy-tools-bar__button--active' : ''}`}
          onClick={() => onToolClick({ id: 'debug', panel: true })}
          title="View debug data (Admin only)"
        >
          <span className="brainstormy-tools-bar__icon">🔧</span>
          <span>Debug</span>
        </button>
      )}
      {isAdmin && (
        <button
          type="button"
          className="brainstormy-tools-bar__button"
          onClick={() => onToolClick({ id: 'writers', path: '/admin?tab=writers' })}
          title="Writers and leads (Admin only)"
        >
          <span>Writers</span>
        </button>
      )}
      <div className="brainstormy-tools-bar__divider" />
      {/* Path v0.1: story-level Guide is unmounted. Path is the next-steps
          door. Series Guide stays disabled (out of this unit). */}
      {hasGuideContext && guideLevel !== 'story' && false && (
        <GuideButton
          isOpen={isGuideOpen}
          onClick={onGuideToggle}
          guideLevel={guideLevel}
        />
      )}
    </div>
  );
}

/**
 * Session Bar - Route Map breadcrumb showing current navigation path
 */
export function SessionBar({ onOpenSettings }) {
  const { currentProject, currentStory, currentSession, projects, stories } = useApp();
  const location = useLocation();

  // Sibling sessions for currentSession's own scope (story- or project-scoped)
  // aren't tracked in AppContext the way projects/stories are, so fetch them
  // the same way the rest of the app already lists sessions for a story/
  // project. currentSession itself can arrive here already fully-loaded
  // (story_id/created_at present, e.g. from ChatPage) or as a lighter sidebar
  // item shape missing those fields (e.g. NavigationTree's click-through,
  // before ChatPage's own fetch replaces it) — in the latter case there's no
  // scope to fetch siblings for, so this quietly skips disambiguation for
  // that brief window rather than guessing.
  const [sessionSiblings, setSessionSiblings] = useState([]);
  const [contextOrderLabel, setContextOrderLabel] = useState(null);
  const sessionScopeId = currentSession?.story_id || currentSession?.project_id || null;
  const storyId = currentStory?.id || null;
  useEffect(() => {
    if (!storyId) {
      setContextOrderLabel(null);
      return undefined;
    }
    let cancelled = false;
    storiesApi.getContextOrder(storyId)
      .then((data) => { if (!cancelled) setContextOrderLabel(data?.label || null); })
      .catch(() => { if (!cancelled) setContextOrderLabel(null); });
    return () => { cancelled = true; };
  }, [storyId]);
  useEffect(() => {
    if (!currentSession?.story_id && !currentSession?.project_id) {
      setSessionSiblings([]);
      return;
    }
    let cancelled = false;
    const fetchSiblings = currentSession.story_id
      ? sessionsApi.list(currentSession.story_id)
      : sessionsApi.listProject(currentSession.project_id);
    fetchSiblings
      .then((data) => { if (!cancelled) setSessionSiblings(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setSessionSiblings([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionScopeId, currentSession?.story_id, currentSession?.project_id]);

  // Build waypoints from current context
  const waypoints = [];
  const projectLabel = currentProject ? disambiguateName(currentProject, projects) : '';

  if (currentProject) {
    waypoints.push({
      label: `📚 ${MEDIUM_CONFIG[currentProject.medium]?.label || 'Projects'}`,
      color: 'var(--color-navy, #2c5a7c)'
    });
    waypoints.push({
      label: projectLabel,
      color: currentProject.accent_color || 'var(--color-gold, #f0c866)'
    });
  }

  // Run 43: a one-book project named like its book showed the title twice.
  const storyLabel = currentStory ? disambiguateName(currentStory, stories) : '';
  if (currentStory && storyLabel.trim() !== projectLabel.trim()) {
    waypoints.push({
      label: storyLabel,
      color: 'var(--color-text-dim, #5c6a78)'
    });
  }

  // You-are-here (#1127): leftover Discovery sitting is not the room
  // when the URL is Scene Writing / Plot Grid / etc. Session name is
  // current only on a Discovery or chat sitting.
  const discoveryIds = [
    currentStory?.discovery_session_id,
    currentProject?.discovery_session_id,
  ].filter(Boolean);
  const sessionId = currentSession?.id || null;
  const path = location.pathname || '';
  const onSessionSitting = isSessionSittingPath(path);
  const roomLabel = breadcrumbRoomFromPath(path, sessionId, discoveryIds);

  if (onSessionSitting && currentSession) {
    waypoints.push({
      label: disambiguateName(currentSession, sessionSiblings),
      color: 'var(--color-success, #4ade80)',
      isCurrent: true
    });
  } else if (roomLabel) {
    waypoints.push({
      label: roomLabel,
      color: 'var(--color-success, #4ade80)',
      isCurrent: true
    });
  } else if (currentSession) {
    waypoints.push({
      label: disambiguateName(currentSession, sessionSiblings),
      color: 'var(--color-success, #4ade80)',
      isCurrent: true
    });
  }

  // Format timestamp
  const formatTimestamp = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="brainstormy-session-bar">
      {/* Route line */}
      <svg className="brainstormy-session-bar__route-line">
        <line
          x1="0"
          y1="1"
          x2="100%"
          y2="1"
          stroke="var(--color-border-mid, #2a4058)"
          strokeWidth="2"
          strokeDasharray="8,5"
          strokeLinecap="round"
        />
      </svg>

      {/* Waypoints */}
      <div className="brainstormy-session-bar__waypoints">
        {waypoints.map((wp, i) => (
          <div
            key={i}
            className={`brainstormy-session-bar__waypoint ${wp.isCurrent ? 'brainstormy-session-bar__waypoint--current' : ''}`}
          >
            <div
              className={`brainstormy-session-bar__dot ${wp.isCurrent ? 'brainstormy-session-bar__dot--current' : ''}`}
              style={{ backgroundColor: wp.color }}
            />
            <span
              className={`brainstormy-session-bar__label ${wp.isCurrent ? 'brainstormy-session-bar__label--current' : ''}`}
            >
              {wp.label}
            </span>
          </div>
        ))}

        {waypoints.length === 0 && (
          <span className="brainstormy-session-bar__label" style={{ fontStyle: 'italic' }}>
            Select a session to begin
          </span>
        )}
        {contextOrderLabel ? (
          <span
            className="brainstormy-session-bar__context-order"
            data-testid="context-order-label"
          >
            {contextOrderLabel}
          </span>
        ) : null}
      </div>

      {/* Fuel Gauge — right of breadcrumbs */}
      <FuelGauge onOpenSettings={onOpenSettings} />

      {/* Timestamp */}
      {currentSession?.updated_at && (
        <div className="brainstormy-session-bar__timestamp">
          <span>🧭</span>
          <span>{formatTimestamp(currentSession.updated_at)}</span>
        </div>
      )}
    </div>
  );
}

// Medium config for session bar
const MEDIUM_CONFIG = {
  novel: { icon: '📚', label: 'Novels' },
};

/**
 * BrainstormyLayout - Main three-panel layout for the Jet Age theme
 */
export default function BrainstormyLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors, isLight, isPaperwhite } = useTheme();
  const clerkAppearance = useClerkAppearance({ avatarBox: 'brainstormy-sidebar__user-avatar' });
  const { currentProject, currentStory, currentSession, setCurrentStoryId } = useApp();

  // Determine Guide context from the active session
  // - If session has story_id → story level (purple GUIDE)
  // - If session exists but no story_id → project level (it's a project session)
  // - If no session but have currentStory → story level
  // - If no session and no currentStory → project level
  const hasGuideContext = currentProject != null;
  const guideLevel = currentSession
    ? (currentSession.story_id ? 'story' : 'project')
    : (currentStory ? 'story' : 'project');

  // Sidebar state with localStorage persistence
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.sidebarWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= SIDEBAR.minWidth && parsed <= SIDEBAR.maxWidth) {
        return parsed;
      }
    }
    return SIDEBAR.defaultWidth;
  });

  const [sidebarZoom, setSidebarZoom] = useState(() => (
    readStoredPercent(localStorage.getItem(STORAGE_KEYS.sidebarZoom), SIDEBAR_TEXT_SIZE)
  ));

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.sidebarCollapsed);
    return saved === 'true';
  });

  // #610: Board does not auto-collapse LeftNav. Collapse is manual and
  // remembered, same as the rest of the app. The outline is a list, not a
  // canvas.

  const [isResizing, setIsResizing] = useState(false);
  const [isSlotResizing, setIsSlotResizing] = useState(false);

  // Derive activeTool from current URL path instead of state
  // This ensures the tool highlight syncs with actual navigation
  const activeTool = useMemo(() => {
    const path = location.pathname;
    // Check if current path matches any tool's path
    for (const tool of TOOLS) {
      if (tool.path && path.startsWith(tool.path)) {
        return tool.id;
      }
      // Also check project-level paths
      if (tool.path && path.startsWith('/project' + tool.path.substring(0))) {
        return tool.id;
      }
    }
    return null;
  }, [location.pathname]);

  // Guide Panel state with localStorage persistence
  const [isGuideOpen, setIsGuideOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.guidePanelOpen);
    return saved === 'true';
  });

  const [guidePanelWidth, setGuidePanelWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.guidePanelWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= GUIDE_PANEL.minWidth && parsed <= GUIDE_PANEL.maxWidth) {
        return parsed;
      }
    }
    return GUIDE_PANEL.defaultWidth;
  });

  // Task Panel state with localStorage persistence
  const [isCapturePanelOpen, setIsCapturePanelOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.capturePanelOpen);
    return saved === 'true';
  });
  const [capturePanelInitialTab, setCapturePanelInitialTab] = useState('actions');

  const [capturePanelWidth, setCapturePanelWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.capturePanelWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= TASK_PANEL.minWidth && parsed <= TASK_PANEL.maxWidth) {
        return parsed;
      }
    }
    return TASK_PANEL.defaultWidth;
  });

  // Bookmarks Panel state with localStorage persistence
  const [isBookmarksPanelOpen, setIsBookmarksPanelOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.bookmarksPanelOpen);
    return saved === 'true';
  });

  const [bookmarksPanelWidth, setBookmarksPanelWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.bookmarksPanelWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= BOOKMARKS_PANEL.minWidth && parsed <= BOOKMARKS_PANEL.maxWidth) {
        return parsed;
      }
    }
    return BOOKMARKS_PANEL.defaultWidth;
  });

  // Learn Panel state with localStorage persistence
  const [isLearnPanelOpen, setIsLearnPanelOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.learnPanelOpen);
    return saved === 'true';
  });

  const [learnPanelWidth, setLearnPanelWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.learnPanelWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= LEARN_PANEL.minWidth && parsed <= LEARN_PANEL.maxWidth) {
        return parsed;
      }
    }
    return LEARN_PANEL.defaultWidth;
  });

  // Learn Panel spawn context (set when opening from gap analysis)
  const [learnSpawnContext, setLearnSpawnContext] = useState(null);

  // Debug Panel state with localStorage persistence (admin-only)
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.debugPanelOpen);
    return saved === 'true';
  });

  const [debugPanelWidth, setDebugPanelWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.debugPanelWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= DEBUG_PANEL.minWidth && parsed <= DEBUG_PANEL.maxWidth) {
        return parsed;
      }
    }
    return DEBUG_PANEL.defaultWidth;
  });

  const [isNamesPanelOpen, setIsNamesPanelOpen] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.namesPanelOpen);
    return saved === 'true';
  });

  const [progressOpen, setProgressOpen] = useState(false);

  const [namesPanelWidth, setNamesPanelWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.namesPanelWidth);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= NAMES_PANEL.minWidth && parsed <= NAMES_PANEL.maxWidth) {
        return parsed;
      }
    }
    return NAMES_PANEL.defaultWidth;
  });

  const [rightSlotWidth, setRightSlotWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem(STORAGE_KEYS.rightSlotWidth) || '', 10);
    const sidebarSaved = parseInt(localStorage.getItem(STORAGE_KEYS.sidebarWidth) || '', 10);
    const sidebar = Number.isFinite(sidebarSaved) ? sidebarSaved : SIDEBAR.defaultWidth;
    const view = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return clampRightSlotWidth(
      Number.isFinite(saved) ? saved : RIGHT_SLOT.defaultWidth,
      view,
      sidebar,
    );
  });

  // Admin status (fetched from settings API)
  const { isAdmin, setIsAdmin } = useApp();

  // Settings dialog state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsFocus, setSettingsFocus] = useState(null);

  const openSettings = useCallback((section) => {
    setSettingsFocus(section || null);
    setIsSettingsOpen(true);
  }, []);

  useEffect(() => registerSettingsOpener(openSettings), [openSettings]);

  // Bug reporter state (Phase 1). Cached beta status paints with the
  // header; until the first answer, the slot is reserved so the gear
  // does not jump when the icon appears.
  const [isBetaTester, setIsBetaTester] = useState(() => readHelpIconCache() === true);
  const [helpIconPending, setHelpIconPending] = useState(() => readHelpIconCache() === null);
  const [showBugReportModal, setShowBugReportModal] = useState(false);
  const [preCapturedScreenshot, setPreCapturedScreenshot] = useState(null);
  const { captureScreenshot } = useBugCapture();

  // Handle bug report button click - show modal immediately, capture screenshot in background
  const handleBugReportClick = async () => {
    // Show modal immediately - don't block on screenshot
    setShowBugReportModal(true);

    // Try to capture screenshot in background with timeout
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Screenshot timeout')), 3000)
      );
      const screenshot = await Promise.race([captureScreenshot(), timeoutPromise]);
      setPreCapturedScreenshot(screenshot);
    } catch (error) {
      console.warn('Screenshot capture failed or timed out:', error);
      setPreCapturedScreenshot(null);
    }
  };

  // Check beta tester status on mount. Who sees the icon is still
  // this response: the cache only repeats the last answer.
  useEffect(() => {
    let cancelled = false;
    bugsApi.checkBetaAccess()
      .then(res => {
        console.log('Beta status API response:', res);
        if (cancelled) return;
        const allowed = Boolean(res && res.is_beta_tester);
        setIsBetaTester(allowed);
        setHelpIconPending(false);
        writeHelpIconCache(allowed);
      })
      .catch((err) => {
        console.error('Beta status API error:', err);
        if (cancelled) return;
        setIsBetaTester(false);
        setHelpIconPending(false);
        writeHelpIconCache(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Note: Display mode is now handled by ThemeContext (dark/light/paperwhite)
  // The jetage theme variables are applied when mode is 'dark'

  // Persist sidebar width
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sidebarWidth, sidebarWidth.toString());
  }, [sidebarWidth]);

  // Persist sidebar zoom
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sidebarZoom, sidebarZoom.toString());
  }, [sidebarZoom]);

  // Settings → Text Size (`zoomLevel`) drives --zoom-level for Outline,
  // Write, and this sidebar. An older saved percent is snapped once.
  useEffect(() => {
    const apply = (raw) => {
      const percent = readChatZoom(raw);
      document.documentElement.style.setProperty('--zoom-level', String(percent / 100));
      if (raw == null || String(raw).trim() === '') return;
      const parsed = parseInt(String(raw), 10);
      if (parsed !== percent) {
        localStorage.setItem(CHAT_TEXT_SIZE.storageKey, String(percent));
      }
    };
    let stored = null;
    try {
      stored = localStorage.getItem(CHAT_TEXT_SIZE.storageKey);
    } catch {
      stored = null;
    }
    apply(stored);
    const onStorage = (event) => {
      if (event.key != null && event.key !== CHAT_TEXT_SIZE.storageKey) return;
      apply(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.sidebarCollapsed, sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Persist Guide Panel state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.guidePanelOpen, isGuideOpen.toString());
  }, [isGuideOpen]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.guidePanelWidth, guidePanelWidth.toString());
  }, [guidePanelWidth]);

  // Persist Task Panel state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.capturePanelOpen, isCapturePanelOpen.toString());
  }, [isCapturePanelOpen]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.capturePanelWidth, capturePanelWidth.toString());
  }, [capturePanelWidth]);

  // Persist Bookmarks Panel state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.bookmarksPanelOpen, isBookmarksPanelOpen.toString());
  }, [isBookmarksPanelOpen]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.bookmarksPanelWidth, bookmarksPanelWidth.toString());
  }, [bookmarksPanelWidth]);

  // Persist Learn Panel state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.learnPanelOpen, isLearnPanelOpen.toString());
  }, [isLearnPanelOpen]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.learnPanelWidth, learnPanelWidth.toString());
  }, [learnPanelWidth]);

  // Persist Debug Panel state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.debugPanelOpen, isDebugPanelOpen.toString());
  }, [isDebugPanelOpen]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.debugPanelWidth, debugPanelWidth.toString());
  }, [debugPanelWidth]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.namesPanelOpen, isNamesPanelOpen.toString());
  }, [isNamesPanelOpen]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.namesPanelWidth, namesPanelWidth.toString());
  }, [namesPanelWidth]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.rightSlotWidth, String(rightSlotWidth));
  }, [rightSlotWidth]);

  // Fetch admin status on mount
  useEffect(() => {
    const fetchAdminStatus = async () => {
      try {
        const response = await settingsApi.getSettings();
        const data = response?.data || response;
        console.log('[BrainstormyLayout] Settings response:', data);
        console.log('[BrainstormyLayout] is_admin value:', data?.is_admin);
        setIsAdmin(data?.is_admin || false);
      } catch (err) {
        console.error('Failed to fetch admin status:', err);
        setIsAdmin(false);
      }
    };
    fetchAdminStatus();
  }, [setIsAdmin]);

  // Sidebar collapse toggle
  const handleToggleCollapse = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // Resize handlers
  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    const raw = parseFloat(document.documentElement.style.getPropertyValue('--zoom-level'));
    const factor = Number.isFinite(raw) && raw > 0 ? raw : 1;
    const visualCap = Math.min(window.innerWidth * 0.48, SIDEBAR.maxWidth * factor);
    const visual = Math.min(visualCap, Math.max(SIDEBAR.minWidth * factor, e.clientX));
    const stored = visual / factor;
    setSidebarWidth(Math.min(SIDEBAR.maxWidth, Math.max(SIDEBAR.minWidth, stored)));
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Global mouse listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Notes buttons and the book menu open one panel in the slot.
  useEffect(() => {
    const panel = location.state?.openReference;
    if (panel !== 'captures' && panel !== 'names' && panel !== 'learn') return;
    setIsCapturePanelOpen(panel === 'captures');
    setIsNamesPanelOpen(panel === 'names');
    setIsLearnPanelOpen(panel === 'learn');
    setIsBookmarksPanelOpen(false);
    setIsDebugPanelOpen(false);
    setProgressOpen(false);
  }, [location.state]);

  // Tool click handler
  const handleToolClick = (tool) => {
    setProgressOpen(false);
    // Handle panel-based tools (Tasks, Bookmarks, Learn, Debug)
    if (tool.panel) {
      if (tool.id === 'captures') {
        // Toggle tasks panel, close others (mutual exclusivity)
        if (isCapturePanelOpen) unfoldRoomChatRef.current();
        setIsCapturePanelOpen(prev => !prev);
        setIsBookmarksPanelOpen(false);
        setIsLearnPanelOpen(false);
        setIsNamesPanelOpen(false);
        setIsDebugPanelOpen(false);
      } else if (tool.id === 'bookmarks') {
        // Toggle bookmarks panel, close others (mutual exclusivity)
        setIsBookmarksPanelOpen(prev => !prev);
        setIsCapturePanelOpen(false);
        setIsLearnPanelOpen(false);
        setIsNamesPanelOpen(false);
        setIsDebugPanelOpen(false);
      } else if (tool.id === 'learn') {
        // Toggle learn panel, close others (mutual exclusivity)
        if (isLearnPanelOpen) unfoldRoomChatRef.current();
        setIsLearnPanelOpen(prev => !prev);
        setIsCapturePanelOpen(false);
        setIsBookmarksPanelOpen(false);
        setIsNamesPanelOpen(false);
        setIsDebugPanelOpen(false);
      } else if (tool.id === 'names') {
        if (isNamesPanelOpen) unfoldRoomChatRef.current();
        setIsNamesPanelOpen(prev => !prev);
        setIsCapturePanelOpen(false);
        setIsBookmarksPanelOpen(false);
        setIsLearnPanelOpen(false);
        setIsDebugPanelOpen(false);
      } else if (tool.id === 'debug') {
        // Toggle debug panel, close others (mutual exclusivity)
        setIsDebugPanelOpen(prev => !prev);
        setIsCapturePanelOpen(false);
        setIsBookmarksPanelOpen(false);
        setIsLearnPanelOpen(false);
        setIsNamesPanelOpen(false);
      }
      return;
    }

    runAfterLeaveGuard(() => {
    // Close all panels when navigating to another tool
    setIsCapturePanelOpen(false);
    setIsBookmarksPanelOpen(false);
    setIsLearnPanelOpen(false);
    setIsNamesPanelOpen(false);
    setIsDebugPanelOpen(false);

    // If already on this tool's page, navigate to chat
    if (activeTool === tool.id) {
      navigate('/chat');
      return;
    }

    if (tool.id === 'plottrack' || (tool.path || '').startsWith('/plottrack')) {
      // Left-map door already encodes the book. Honor that URL so first
      // paint is that book, not last-in-series or most-recent (#849).
      const alreadyBook = /^\/plottrack\/[^/]+/.test(tool.path || '');
      navigate(alreadyBook
        ? tool.path
        : plottrackPathForBook({
          pathname: location.pathname,
          currentStory,
          currentSession,
        }));
      return;
    }

    if (tool.id === 'path' || (tool.path || '').startsWith('/tasks') || (tool.path || '').startsWith('/path')) {
      const alreadyBook = /^\/(?:tasks|path)\/[^/]+/.test(tool.path || '');
      navigate(alreadyBook
        ? tool.path
        : pathRoomPathForBook({
          pathname: location.pathname,
          currentStory,
          currentSession,
        }));
      return;
    }

    if (tool.id === 'style-cards' || (tool.path || '').startsWith('/style-cards')) {
      const alreadyBook = /^\/style-cards\/[^/]+/.test(tool.path || '');
      const path = alreadyBook
        ? tool.path
        : styleCardsPathForBook({
          pathname: location.pathname,
          currentStory,
          currentSession,
        });
      if (path) navigate(path);
      return;
    }

    if (tool.id === 'story-rules' || tool.path === '/story-rules') {
      navigate(storyRulesPath(currentStory?.id));
      return;
    }

    if (tool.id === 'scene-writing' || tool.path === '/scene-writing' || (tool.path || '').startsWith('/scene-writing')) {
      navigate(sceneWritingPathForBook({
        pathname: location.pathname,
        currentStory,
        currentSession,
      }));
      return;
    }

    navigate(tool.path);
    });
  };

  const closeReferencePanels = () => {
    setIsCapturePanelOpen(false);
    setIsBookmarksPanelOpen(false);
    setIsLearnPanelOpen(false);
    setIsNamesPanelOpen(false);
    setIsDebugPanelOpen(false);
  };

  const handleSlotResizeMove = useCallback((e) => {
    if (!isSlotResizing) return;
    const raw = parseFloat(document.documentElement.style.getPropertyValue('--zoom-level'));
    const factor = Number.isFinite(raw) && raw > 0 ? raw : 1;
    const chrome = sidebarCollapsed
      ? SIDEBAR.collapsedWidth
      : Math.min(window.innerWidth * 0.48, sidebarWidth * factor);
    setRightSlotWidth(clampRightSlotWidth(
      window.innerWidth - e.clientX,
      window.innerWidth,
      chrome,
    ));
  }, [isSlotResizing, sidebarCollapsed, sidebarWidth]);

  const handleSlotResizeEnd = useCallback(() => {
    setIsSlotResizing(false);
  }, []);

  useEffect(() => {
    if (!isSlotResizing) return undefined;
    document.addEventListener('mousemove', handleSlotResizeMove);
    document.addEventListener('mouseup', handleSlotResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleSlotResizeMove);
      document.removeEventListener('mouseup', handleSlotResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isSlotResizing, handleSlotResizeMove, handleSlotResizeEnd]);

  // Guide Panel handlers
  const handleGuideToggle = useCallback(() => {
    setIsGuideOpen(prev => !prev);
  }, []);

  const handleGuideClose = useCallback(() => {
    setIsGuideOpen(false);
  }, []);

  const handleGuidePanelWidthChange = useCallback((newWidth) => {
    const clamped = Math.min(GUIDE_PANEL.maxWidth, Math.max(GUIDE_PANEL.minWidth, newWidth));
    setGuidePanelWidth(clamped);
  }, []);

  // Handle spawning a new session from Guide Panel
  const handleSpawnSession = useCallback((sessionData) => {
    console.log('handleSpawnSession called with:', sessionData);
    // Navigate to the new session when spawned
    if (sessionData?.id) {
      console.log('Navigating to:', `/chat/${sessionData.id}`);
      // Close the Guide panel so user can see the new session
      setIsGuideOpen(false);
      navigate(`/chat/${sessionData.id}`);
    } else {
      console.warn('No session ID in sessionData:', sessionData);
    }
  }, [navigate]);

  // Handle navigating to a newly created story from Project Guide
  const handleNavigateToStory = useCallback((storyId, guideSession) => {
    // Set the current story
    if (setCurrentStoryId) {
      setCurrentStoryId(storyId);
    }
    // If a guide session was created, we could navigate to it
    // For now, just keep the Guide panel open (it will reload with story context)
    // The story selection will trigger a re-render with the new story context
  }, [setCurrentStoryId]);

  // Task Panel handlers
  const unfoldRoomChatRef = useRef(() => {});
  const handleRightSlotSelectRef = useRef(() => {});

  const handleCapturePanelClose = useCallback(() => {
    setIsCapturePanelOpen(false);
    setCapturePanelInitialTab('actions');
    unfoldRoomChatRef.current();
  }, []);

  // Bookmarks Panel handlers
  const handleBookmarksPanelClose = useCallback(() => {
    setIsBookmarksPanelOpen(false);
  }, []);

  const handleBookmarksPanelWidthChange = useCallback((newWidth) => {
    const clamped = Math.min(BOOKMARKS_PANEL.maxWidth, Math.max(BOOKMARKS_PANEL.minWidth, newWidth));
    setBookmarksPanelWidth(clamped);
  }, []);

  const handleCapturePanelWidthChange = useCallback((newWidth) => {
    const clamped = Math.min(TASK_PANEL.maxWidth, Math.max(TASK_PANEL.minWidth, newWidth));
    setCapturePanelWidth(clamped);
  }, []);

  // Handle navigating to a session from Task Panel
  const handleNavigateToSession = useCallback((sessionId) => {
    if (sessionId) {
      // Close task panel when navigating
      setIsCapturePanelOpen(false);
      navigate(`/chat/${sessionId}`);
    }
  }, [navigate]);

  // Learn Panel handlers
  const handleLearnPanelClose = useCallback(() => {
    setIsLearnPanelOpen(false);
    setLearnSpawnContext(null);
    unfoldRoomChatRef.current();
  }, []);

  const handleLearnPanelWidthChange = useCallback((newWidth) => {
    const clamped = Math.min(LEARN_PANEL.maxWidth, Math.max(LEARN_PANEL.minWidth, newWidth));
    setLearnPanelWidth(clamped);
  }, []);

  // Handle spawning a Learn conversation from Guide Panel (Task 3.1)
  const handleSpawnLearnConversation = useCallback((spawnContext) => {
    // Close other panels, open Learn panel with spawn context
    setIsCapturePanelOpen(false);
    setIsBookmarksPanelOpen(false);
    setIsNamesPanelOpen(false);
    setLearnSpawnContext(spawnContext);
    setIsLearnPanelOpen(true);
  }, []);

  // Debug Panel handlers
  const handleDebugPanelClose = useCallback(() => {
    setIsDebugPanelOpen(false);
  }, []);

  const handleDebugPanelWidthChange = useCallback((newWidth) => {
    const clamped = Math.min(DEBUG_PANEL.maxWidth, Math.max(DEBUG_PANEL.minWidth, newWidth));
    setDebugPanelWidth(clamped);
  }, []);

  const handleNamesPanelClose = useCallback(() => {
    setIsNamesPanelOpen(false);
    unfoldRoomChatRef.current();
  }, []);

  const handleProgressClose = useCallback(() => {
    setProgressOpen(false);
    unfoldRoomChatRef.current();
  }, []);

  const handleNamesPanelWidthChange = useCallback((newWidth) => {
    const clamped = Math.min(NAMES_PANEL.maxWidth, Math.max(NAMES_PANEL.minWidth, newWidth));
    setNamesPanelWidth(clamped);
  }, []);

  // Calculate zoom scale for sidebar content
  const zoomScale = sidebarZoom / 100;

  // Mobile layout
  const breakpoint = useBreakpoint();
  const isMobileView = breakpoint === 'mobile';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [roomChatSheet, setRoomChatSheet] = useState(false);
  const [storyProgressOpen, setStoryProgressOpen] = useState(false);
  const roomChatCloseRef = useRef(null);
  const storyProgressBtnRef = useRef(null);
  const storyProgressOpenRef = useRef(false);
  storyProgressOpenRef.current = storyProgressOpen;
  const roomSubject = chatPaneSubject(location.pathname);
  // The pane unmounts when another right-panel choice opens, and the
  // phone sheet unmounts when it closes. The unsent line stays here.
  const [roomDrafts, setRoomDrafts] = useState({});
  const setRoomDraft = useCallback((key, text) => {
    setRoomDrafts((prev) => {
      const value = String(text ?? '');
      if (!value) {
        if (!Object.prototype.hasOwnProperty.call(prev, key)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === value) return prev;
      return { ...prev, [key]: value };
    });
  }, []);
  const roomChat = roomSubject ? (
    <RoomChatDraftProvider
      storyId={currentStory?.id || null}
      roomKey={roomKeyForSubject(roomSubject)}
      drafts={roomDrafts}
      onChange={setRoomDraft}
    >
      <RoomChatPane subject={roomSubject} />
    </RoomChatDraftProvider>
  ) : null;

  // Dedicated panel open handlers for mobile (mutual exclusivity)
  const handleCapturePanelOpen = useCallback(() => {
    setCapturePanelInitialTab('actions');
    setIsCapturePanelOpen(true);
    setIsBookmarksPanelOpen(false);
    setIsLearnPanelOpen(false);
    setIsNamesPanelOpen(false);
    setIsDebugPanelOpen(false);
  }, []);

  const handleSavedIdeasOpen = useCallback(() => {
    setCapturePanelInitialTab('ideas');
    setIsCapturePanelOpen(true);
    setIsBookmarksPanelOpen(false);
    setIsLearnPanelOpen(false);
    setIsNamesPanelOpen(false);
    setIsDebugPanelOpen(false);
  }, []);

  const handleBookmarksPanelOpen = useCallback(() => {
    setIsBookmarksPanelOpen(true);
    setIsCapturePanelOpen(false);
    setIsLearnPanelOpen(false);
    setIsNamesPanelOpen(false);
    setIsDebugPanelOpen(false);
  }, []);

  const handleLearnPanelOpen = useCallback(() => {
    setIsLearnPanelOpen(true);
    setIsCapturePanelOpen(false);
    setIsBookmarksPanelOpen(false);
    setIsNamesPanelOpen(false);
    setIsDebugPanelOpen(false);
  }, []);

  // Mobile panel overlay helper
  const getActiveMobilePanel = () => {
    if (isCapturePanelOpen) return { id: 'captures', title: 'Compendium', close: handleCapturePanelClose };
    if (isBookmarksPanelOpen) return { id: 'bookmarks', title: 'Bookmarks', close: handleBookmarksPanelClose };
    if (isLearnPanelOpen) return { id: 'learn', title: 'Learn', close: handleLearnPanelClose };
    if (isNamesPanelOpen) return { id: 'names', title: 'Names', close: handleNamesPanelClose };
    if (isDebugPanelOpen) return { id: 'debug', title: 'Debug', close: handleDebugPanelClose };
    return null;
  };

  const activeMobilePanel = isMobileView ? getActiveMobilePanel() : null;

  useEffect(() => {
    if (!isMobileView || !roomSubject) setRoomChatSheet(false);
  }, [isMobileView, roomSubject]);

  useEffect(() => {
    if (!roomChatSheet) setStoryProgressOpen(false);
  }, [roomChatSheet]);

  useEffect(() => {
    if (!roomChatSheet) return undefined;
    roomChatCloseRef.current?.focus();
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (storyProgressOpenRef.current) return;
      setRoomChatSheet(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [roomChatSheet]);

  const openRoomChatSheet = useCallback(() => {
    if (chatPaneSubject(location.pathname)) setRoomChatSheet(true);
  }, [location.pathname]);

  const goToIdeaChat = useCallback(() => {
    runAfterLeaveGuard(() => {
      setRoomChatSheet(false);
      const idea = openBookChatPath({ currentSession, currentStory, currentProject });
      if (idea) navigate(idea);
    });
  }, [navigate, currentSession, currentStory, currentProject]);

  const rightSlotOn = !isMobileView && showRightPanelSlot(location.pathname);
  unfoldRoomChatRef.current = () => {
    if (bookToolShouldUnfoldChat(location.pathname, isMobileView)) publishChatUnfold();
  };
  const openReferenceId = (
    (isCapturePanelOpen && currentProject && 'captures')
    || (isLearnPanelOpen && currentProject && 'learn')
    || (isNamesPanelOpen && 'names')
    || (isBookmarksPanelOpen && currentProject && 'bookmarks')
    || (isDebugPanelOpen && isAdmin && 'debug')
    || null
  );
  const progressFeature = progressOpen && currentStory ? 'progress' : null;
  const rightFeature = openReferenceId || progressFeature || (roomSubject ? 'chat' : null);

  // Chat returns the room thread. The other buttons use the same
  // open path as the book menu and the Notes buttons.
  const handleRightSlotSelect = (id) => {
    if (id === 'progress') {
      if (rightFeature === 'progress') {
        setProgressOpen(false);
        unfoldRoomChatRef.current();
        return;
      }
      closeReferencePanels();
      setProgressOpen(true);
      return;
    }
    setProgressOpen(false);
    if (id === 'chat') {
      closeReferencePanels();
      unfoldRoomChatRef.current();
      return;
    }
    handleToolClick({ id, panel: true });
  };
  handleRightSlotSelectRef.current = handleRightSlotSelect;

  useEffect(() => {
    const onTool = (event) => {
      const id = event?.detail;
      if (typeof id !== 'string' || !id) return;
      handleRightSlotSelectRef.current(id);
    };
    window.addEventListener(BOOK_TOOLS_EVENT, onTool);
    return () => window.removeEventListener(BOOK_TOOLS_EVENT, onTool);
  }, []);

  useEffect(() => {
    if (!rightSlotOn || !rightFeature) return undefined;
    const raw = parseFloat(document.documentElement.style.getPropertyValue('--zoom-level'));
    const factor = Number.isFinite(raw) && raw > 0 ? raw : 1;
    const chrome = sidebarCollapsed
      ? SIDEBAR.collapsedWidth
      : Math.min(window.innerWidth * 0.48, sidebarWidth * factor);
    const fit = () => {
      setRightSlotWidth((current) => clampRightSlotWidth(current, window.innerWidth, chrome));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [rightSlotOn, rightFeature, sidebarCollapsed, sidebarWidth]);

  const bookToolsRoute = showBookToolsRow(location.pathname);
  const showSlot = rightSlotOn && (!bookToolsRoute || Boolean(rightFeature));
  const bookToolsValue = {
    activeId: (
      rightFeature === 'names'
      || rightFeature === 'learn'
      || rightFeature === 'captures'
      || rightFeature === 'progress'
    ) ? rightFeature : null,
    available: Boolean(rightSlotOn && bookToolsRoute),
    namesEnabled: Boolean(currentStory),
    referenceEnabled: Boolean(currentProject),
    progressEnabled: Boolean(currentStory),
    select: (id) => handleRightSlotSelectRef.current(id),
  };

  return (
    <div className="brainstormy-layout">
      {/* Texture overlay */}
      <div className="brainstormy-layout__texture" />

      {/* Mobile Header */}
      {isMobileView && (
        <MobileHeader
          onMenuOpen={() => setDrawerOpen(true)}
          onOpenCaptures={handleCapturePanelOpen}
          onOpenBookmarks={handleBookmarksPanelOpen}
          onOpenLearn={handleLearnPanelOpen}
          onOpenSettings={openSettings}
        />
      )}

      {/* Mobile Drawer */}
      {isMobileView && (
        <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <SidebarTabs
            onSessionSelect={() => setDrawerOpen(false)}
            onOpenSavedIdeas={handleSavedIdeasOpen}
          />
        </MobileDrawer>
      )}

      {/* Left Sidebar (desktop only) */}
      {!isMobileView && (
      <div
        className={`brainstormy-sidebar ${sidebarCollapsed ? 'brainstormy-sidebar--collapsed' : ''}`}
        style={{
          width: sidebarCollapsed
            ? SIDEBAR.collapsedWidth
            : `calc(${sidebarWidth}px * var(--zoom-level, 1))`,
          maxWidth: sidebarCollapsed
            ? SIDEBAR.collapsedWidth
            : `calc(${SIDEBAR.maxWidth}px * var(--zoom-level, 1))`,
        }}
      >
        <SidebarHeader
          onSettingsClick={() => setIsSettingsOpen(true)}
          showBugButton={isBetaTester}
          reserveBugSlot={helpIconPending}
          onBugReportClick={handleBugReportClick}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
          sidebarWidth={sidebarWidth}
        />

        {/* Navigation — Atlas left nav. LeftNav renders BOTH states: the
            expanded column and (when collapsed) the pictogram rail with a
            findable Show left panel tab (#802). Collapse is remembered. */}
        <div className="brainstormy-sidebar__content">
          <div
            className="brainstormy-sidebar__content-scaler"
            style={{
              // Own stepper. CSS multiplies this by Settings --zoom-level.
              // Width stays 100%: Chrome lays text out in width/zoom, so
              // dividing by --zoom-level as well squeezed labels to one letter.
              // No min-height: zoom times 100% overflowed this pane (#772).
              '--sidebar-zoom': sidebarCollapsed ? '1' : String(zoomScale),
            }}
          >
            <LeftNav
              onToolClick={handleToolClick}
              isCapturePanelOpen={isCapturePanelOpen}
              isLearnPanelOpen={isLearnPanelOpen}
              isBookmarksPanelOpen={isBookmarksPanelOpen}
              hasStory={!!currentStory}
              collapsed={sidebarCollapsed}
              onToggleCollapse={handleToggleCollapse}
            />
          </div>
          {/* Balance scrolls with the nav (#2430). Pinned under the
              scroller, it covered Docs once zoom grew the list. */}
          {!sidebarCollapsed && !showLegacyAppBar(location.pathname, currentStory?.id) && (
            <div className="brainstormy-sidebar__balance" data-testid="sidebar-balance">
              <FuelGauge onOpenSettings={openSettings} />
            </div>
          )}
        </div>

        {/* Collapsed chrome footer: settings / user / bug (the rail's nav lives
            in LeftNav above; these are app-chrome controls). */}
        {sidebarCollapsed && (
          <div className="brainstormy-sidebar__collapsed-icons brainstormy-sidebar__collapsed-icons--footer">
            {isBetaTester ? (
              <RailTooltip label="Help and feedback">
                <HelpFeedbackButton
                  visible
                  onClick={handleBugReportClick}
                  className="brainstormy-sidebar__collapsed-btn"
                />
              </RailTooltip>
            ) : helpIconPending ? (
              <HelpFeedbackButton
                reserved
                className="brainstormy-sidebar__collapsed-btn"
              />
            ) : null}
            <RailTooltip label="Settings">
              <button
                className="brainstormy-sidebar__collapsed-btn"
                onClick={() => setIsSettingsOpen(true)}
                aria-label="Settings"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            </RailTooltip>
            <RailTooltip label="Account">
              <div className="brainstormy-sidebar__collapsed-user">
                <UserButton
                  afterSignOutUrl="/sign-in"
                  appearance={clerkAppearance}
                />
              </div>
            </RailTooltip>
          </div>
        )}

        {/* Zoom slider */}
        {!sidebarCollapsed && (
          <div className="brainstormy-sidebar__footer">
            <span className="brainstormy-sidebar__zoom-label">A</span>
            <TextSizeStepper
              value={sidebarZoom}
              onChange={setSidebarZoom}
              min={SIDEBAR.minZoom}
              max={SIDEBAR.maxZoom}
              step={SIDEBAR_TEXT_SIZE.step}
            />
          </div>
        )}

        {/* Gradient bar */}
        <div className="brainstormy-sidebar__gradient" />

        {/* Resize handle - only when not collapsed */}
        {!sidebarCollapsed && (
          <div
            className={`brainstormy-sidebar__resize-handle ${isResizing ? 'brainstormy-sidebar__resize-handle--active' : ''}`}
            onMouseDown={handleMouseDown}
          >
            <div className="brainstormy-sidebar__resize-dots">
              <div className="brainstormy-sidebar__resize-dot" />
              <div className="brainstormy-sidebar__resize-dot" />
              <div className="brainstormy-sidebar__resize-dot" />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Main Content Area */}
      <div className="brainstormy-main">
        {/* Top ToolsBar — Reference + Find destinations (Compendium · Research ·
            Names · Search · Story Dashboard). Book-tool rooms, including
            Series Docs, keep those doors in the title row. This bar stays
            off there so the same buttons are not drawn twice. */}
        {!isMobileView && showLegacyAppBar(location.pathname, currentStory?.id) && !bookToolsRoute && (
          <ToolsBar
            activeTool={activeTool}
            isCapturePanelOpen={isCapturePanelOpen}
            isBookmarksPanelOpen={isBookmarksPanelOpen}
            isLearnPanelOpen={isLearnPanelOpen}
            isNamesPanelOpen={isNamesPanelOpen}
            isDebugPanelOpen={isDebugPanelOpen}
            hasProject={!!currentProject}
            hasStory={!!currentStory}
            isAdmin={isAdmin}
            onToolClick={handleToolClick}
            isGuideOpen={isGuideOpen}
            onGuideToggle={handleGuideToggle}
            guideLevel={guideLevel}
            hasGuideContext={hasGuideContext}
          />
        )}

        {/* Session Bar (desktop only) */}
        {!isMobileView && showLegacyAppBar(location.pathname, currentStory?.id) && (
        <SessionBar
          onOpenSettings={openSettings}
        />
        )}

        {/* Content */}
        <div className="brainstormy-content">
          <div className="brainstormy-chat">
            {typeof location.state?.restoreNotice === 'string' && location.state.restoreNotice && (
              <p className="brainstormy-restore-notice" role="status" data-testid="restore-notice">
                {location.state.restoreNotice}
              </p>
            )}
            {Array.isArray(location.state?.restoreWarnings) && location.state.restoreWarnings.map((warning, index) => (
              typeof warning === 'string' && warning
                ? (
                  <p
                    key={`restore-warning-${index}`}
                    className="brainstormy-restore-notice"
                    role="status"
                  >
                    {warning}
                  </p>
                )
                : null
            ))}
            <BookToolsProvider value={bookToolsValue}>
              {children || <Outlet />}
            </BookToolsProvider>
            {!(location.pathname === '/plottrack' || location.pathname.startsWith('/plottrack/')) && (
              <FrameworkUndoToast held />
            )}
          </div>
          {!isMobileView && !rightSlotOn && roomChat}

          {/* Path v0.1: story-level Guide panel is unmounted. Series Guide
              stays disabled (out of this unit). */}
          {isGuideOpen && hasGuideContext && guideLevel !== 'story' && false && (
            <GuidePanel
              storyId={guideLevel === 'story' ? (currentSession?.story_id || currentStory?.id) : null}
              projectId={currentProject?.id}
              guideLevel={guideLevel}
              width={guidePanelWidth}
              onWidthChange={handleGuidePanelWidthChange}
              onClose={handleGuideClose}
              onSpawnSession={handleSpawnSession}
              onSpawnLearnConversation={handleSpawnLearnConversation}
              onNavigateToStory={handleNavigateToStory}
            />
          )}

          {/* Task Panel (desktop only). Book rooms use the slot below. */}
          {!isMobileView && !rightSlotOn && isCapturePanelOpen && currentProject && (
            <CapturePanel
              storyId={currentStory?.id}
              projectId={currentProject?.id}
              project={currentProject}
              width={capturePanelWidth}
              onWidthChange={handleCapturePanelWidthChange}
              onClose={handleCapturePanelClose}
              onNavigateToSession={handleNavigateToSession}
            />
          )}

          {/* Bookmarks Panel (desktop only) */}
          {!isMobileView && !rightSlotOn && isBookmarksPanelOpen && currentProject && (
            <BookmarksPanel
              storyId={currentSession?.story_id || currentStory?.id}
              projectId={currentProject?.id}
              width={bookmarksPanelWidth}
              onWidthChange={handleBookmarksPanelWidthChange}
              onClose={handleBookmarksPanelClose}
            />
          )}

          {/* Learn Panel (desktop only) */}
          {!isMobileView && !rightSlotOn && isLearnPanelOpen && currentProject && (
            <LearnPanel
              projectId={currentProject.id}
              spawnContext={learnSpawnContext}
              width={learnPanelWidth}
              onWidthChange={handleLearnPanelWidthChange}
              onClose={handleLearnPanelClose}
            />
          )}

          {!isMobileView && !rightSlotOn && isNamesPanelOpen && (
            <NameWizardPanel
              storyId={currentStory?.id}
              width={namesPanelWidth}
              onWidthChange={handleNamesPanelWidthChange}
              onClose={handleNamesPanelClose}
            />
          )}

          {/* Debug Panel (Admin only, desktop only) */}
          {!isMobileView && !rightSlotOn && isDebugPanelOpen && isAdmin && (
            <DebugPanel
              width={debugPanelWidth}
              onWidthChange={handleDebugPanelWidthChange}
              onClose={handleDebugPanelClose}
            />
          )}

          {showSlot && (
            <aside
              className={`right-slot${rightFeature ? '' : ' right-slot--chooser'}`}
              style={rightFeature ? {
                width: rightSlotWidth,
                minWidth: rightSlotWidth,
                flexShrink: 0,
                '--right-slot-width': `${rightSlotWidth}px`,
              } : undefined}
              data-testid="right-slot"
              data-feature={rightFeature || 'none'}
              aria-label="Right panel"
            >
              {rightFeature && (
                <div
                  className={`right-slot__resize${isSlotResizing ? ' right-slot__resize--active' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    setIsSlotResizing(true);
                  }}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize right panel"
                  data-testid="right-slot-resize"
                />
              )}
              {!bookToolsRoute && (
              <RightPanelSwitcher
                activeId={rightFeature}
                includeChat={Boolean(roomSubject)}
                namesEnabled={Boolean(currentStory)}
                referenceEnabled={Boolean(currentProject)}
                progressEnabled={Boolean(currentStory)}
                onSelect={handleRightSlotSelect}
              />
              )}
              {rightFeature === 'progress' && currentStory && (
                <div className="right-slot__body">
                  <StoryProgressPanel
                    storyId={currentStory.id}
                    textScale={zoomScale}
                    onClose={handleProgressClose}
                  />
                </div>
              )}
              {rightFeature === 'chat' && roomChat && (
                <div className="right-slot__body">
                  {roomChat}
                </div>
              )}
              {rightFeature === 'captures' && currentProject && (
                <div className="right-slot__body">
                  <CapturePanel
                    storyId={currentStory?.id}
                    projectId={currentProject?.id}
                    project={currentProject}
                    width={rightSlotWidth}
                    onWidthChange={setRightSlotWidth}
                    onClose={handleCapturePanelClose}
                    onNavigateToSession={handleNavigateToSession}
                  />
                </div>
              )}
              {rightFeature === 'bookmarks' && currentProject && (
                <div className="right-slot__body">
                  <BookmarksPanel
                    storyId={currentSession?.story_id || currentStory?.id}
                    projectId={currentProject?.id}
                    width={rightSlotWidth}
                    onWidthChange={setRightSlotWidth}
                    onClose={handleBookmarksPanelClose}
                  />
                </div>
              )}
              {rightFeature === 'learn' && currentProject && (
                <div className="right-slot__body">
                  <LearnPanel
                    projectId={currentProject.id}
                    spawnContext={learnSpawnContext}
                    width={rightSlotWidth}
                    onWidthChange={setRightSlotWidth}
                    onClose={handleLearnPanelClose}
                  />
                </div>
              )}
              {rightFeature === 'names' && (
                <div className="right-slot__body">
                  <NameWizardPanel
                    storyId={currentStory?.id}
                    width={rightSlotWidth}
                    onWidthChange={setRightSlotWidth}
                    onClose={handleNamesPanelClose}
                  />
                </div>
              )}
              {rightFeature === 'debug' && isAdmin && (
                <div className="right-slot__body">
                  <DebugPanel
                    width={rightSlotWidth}
                    onWidthChange={setRightSlotWidth}
                    onClose={handleDebugPanelClose}
                  />
                </div>
              )}
            </aside>
          )}
        </div>
      </div>

      {/* Mobile Panel Overlay */}
      {activeMobilePanel && (
        <div className="mobile-panel-overlay">
          <div className="mobile-panel-header">
            <span className="mobile-panel-title">{activeMobilePanel.title}</span>
            <button type="button" className="mobile-panel-close" onClick={activeMobilePanel.close}>✕</button>
          </div>
          <div className="mobile-panel-content">
            {activeMobilePanel.id === 'captures' && currentProject && (
              <CapturePanel
                storyId={currentStory?.id}
                projectId={currentProject?.id}
                project={currentProject}
                initialTab={capturePanelInitialTab}
                onClose={handleCapturePanelClose}
                onNavigateToSession={handleNavigateToSession}
              />
            )}
            {activeMobilePanel.id === 'bookmarks' && currentProject && (
              <BookmarksPanel
                storyId={currentSession?.story_id || currentStory?.id}
                projectId={currentProject?.id}
                onClose={handleBookmarksPanelClose}
              />
            )}
            {activeMobilePanel.id === 'learn' && currentProject && (
              <LearnPanel
                projectId={currentProject.id}
                spawnContext={learnSpawnContext}
                onClose={handleLearnPanelClose}
              />
            )}
            {activeMobilePanel.id === 'names' && (
              <NameWizardPanel
                storyId={currentStory?.id}
                onClose={handleNamesPanelClose}
              />
            )}
            {activeMobilePanel.id === 'debug' && isAdmin && (
              <DebugPanel
                onClose={handleDebugPanelClose}
              />
            )}
          </div>
        </div>
      )}

      {/* Phone room chat. Desktop keeps the side pane above. */}
      {isMobileView && roomChatSheet && roomSubject && (
        <div
          className="mobile-room-chat"
          role="dialog"
          aria-modal="true"
          aria-label="Chat"
          data-testid="mobile-room-chat"
        >
          <div className="mobile-room-chat__bar">
            <button
              type="button"
              className="mobile-room-chat__close"
              ref={roomChatCloseRef}
              onClick={() => setRoomChatSheet(false)}
              aria-label="Close"
            >
              ×
            </button>
            <button
              type="button"
              className="mobile-room-chat__progress"
              ref={storyProgressBtnRef}
              aria-haspopup="dialog"
              aria-expanded={storyProgressOpen}
              onClick={() => setStoryProgressOpen(true)}
            >
              {STORY_PROGRESS_SHEET_TITLE}
            </button>
            <button
              type="button"
              className="mobile-room-chat__idea"
              onClick={goToIdeaChat}
              disabled={!openBookChatPath({ currentSession, currentStory, currentProject })}
            >
              {IDEA_ROOM_TITLE}
            </button>
          </div>
          {roomChat}
          <StoryProgressSheet
            open={storyProgressOpen}
            onClose={() => {
              setStoryProgressOpen(false);
              storyProgressBtnRef.current?.focus();
            }}
            storyId={currentStory?.id || null}
            textScale={zoomScale}
          />
        </div>
      )}

      {/* Mobile Tab Bar */}
      {isMobileView && (
        <MobileTabBar onOpenRoomChat={openRoomChatSheet} chatOpen={roomChatSheet} />
      )}

      <BookUploadDoor />

      {/* Settings Dialog */}
      <SettingsDialog
        isOpen={isSettingsOpen}
        focusSection={settingsFocus}
        onClose={() => {
          setIsSettingsOpen(false);
          setSettingsFocus(null);
        }}
      />

      {/* Bug Reporter Modal (Beta Testers Only) */}
      {isBetaTester && showBugReportModal && (
        <BugReportModal
          onClose={() => setShowBugReportModal(false)}
          onSuccess={() => setShowBugReportModal(false)}
          preCapturedScreenshot={preCapturedScreenshot}
        />
      )}
    </div>
  );
}
